import { useMutation } from 'urql'
import { useState, useEffect, useCallback } from 'react'
import { InsertMediaDocument } from '@/app/profile/settings/(gql)/update-user-profile.graphql'
import { getPresignedImageUploadUrl } from '@/app/actions/get-presigned-url'

/**
 * A hook for handling image uploads to S3.
 * It manages file selection, preview, upload progress, and error handling.
 *
 * @param onUploadComplete - A callback function that is called with the media ID upon successful upload and record insertion.
 * @returns An object containing:
 *  - `handleFileSelect`: (event: React.ChangeEvent<HTMLInputElement>) => Promise<void> - A function to handle the file input's change event.
 *  - `previewUrl`: string | null - A URL for the selected image file, for preview purposes. This is cleaned up automatically.
 *  - `isUploading`: boolean - A state that is true while the image is being uploaded.
 *  - `error`: string | null - An error message if the upload fails, otherwise null.
 *  - `reset`: () => void - A function to reset the preview URL and error state.
 */
export function useS3ImageUpload({ onUploadComplete }: { onUploadComplete: (id: number) => void }): {
  handleFileSelect: (event: React.ChangeEvent<HTMLInputElement>) => Promise<void>
  previewUrl: string | null
  isUploading: boolean
  error: string | null
  reset: () => void
} {
  const [isUploading, setIsUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [, insertMedia] = useMutation(InsertMediaDocument)

  // Cleanup preview URL when component unmounts or preview changes
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl)
      }
    }
  }, [previewUrl])

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    setError(null)

    try {
      setIsUploading(true)

      // Create preview URL for the selected file
      const objectUrl = URL.createObjectURL(file)
      setPreviewUrl(objectUrl)

      const res = await getPresignedImageUploadUrl({
        fileType: file.type,
        fileSize: file.size,
        keyPath: 'users/'
      })

      if (!res.success) {
        throw new Error(res.error)
      }

      const { url, key } = res

      const uploadResponse = await fetch(url, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type
        }
      })

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload image')
      }

      // Insert media record
      const result = await insertMedia({
        media: {
          bucket_name: process.env.NEXT_PUBLIC_IMAGE_UPLOAD_S3_BUCKET || '',
          file_path: key
        }
      })

      if (result.data?.insert_media_one?.id) {
        onUploadComplete(result.data?.insert_media_one?.id)
      } else {
        throw new Error('Failed to insert media record')
      }
    } catch (error) {
      console.error('Upload error:', error)
      setError(error instanceof Error ? error.message : 'An unknown error occurred')
      setPreviewUrl(null)
    } finally {
      setIsUploading(false)
    }
  }

  const reset = useCallback(() => {
    setPreviewUrl(null)
    setError(null)
  }, [])

  return {
    isUploading,
    handleFileSelect,
    previewUrl,
    reset,
    error
  }
}
